import { useMutation } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DiscordTemplateInstallDialog } from './DiscordTemplateInstall.dialog';
import {
  DISCORD_CATEGORIES,
  DISCORD_SCRATCH_TEMPLATE,
  DISCORD_TEMPLATES,
} from './discordTemplates.const';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  DiscordTemplateInstallInput,
  DiscordTemplateInstallResponse,
} from '@/Domain/api/Action/dto/DiscordTemplate.dto';
import { ActionTemplateGallery } from '@/Domain/components/Action/ActionTemplateGallery/ActionTemplateGallery';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { useDiscordTokenStatus } from '@/Domain/hooks/Integration/useDiscordTokenStatus.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  CHANNEL_DISCORD,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';
import { SCENARIO_ID } from '@/context';

export const DiscordTemplateGalleryDialog = () => {
  const { isOpen } = useModalStore();

  if (!isOpen(MODAL_NAMES.DISCORD_TEMPLATE_GALLERY_DIALOG)) return null;

  return <DiscordTemplateGalleryDialogContent />;
};

const DiscordTemplateGalleryDialogContent = () => {
  const { t } = useTranslation('actions_page');
  const { hasToken, isFetching: isTokenStatusFetching } = useDiscordTokenStatus();
  const { closeModal } = useModalStore();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<
    (typeof DISCORD_TEMPLATES)[number] | null
  >(null);
  const installMutation = useMutation<
    DiscordTemplateInstallResponse,
    unknown,
    DiscordTemplateInstallInput
  >({
    mutationKey: [QueryKey.actions.discordTemplates.install],
  });

  const goToDiscordSetup = () => {
    closeModal();
    navigate(
      `/i/${SCENARIO_ID}/${IndexPath.channels}?${OPEN_MODAL_QUERY_PARAM}=${CHANNEL_DISCORD}`,
    );
  };

  const selectTemplate = async (template: (typeof DISCORD_TEMPLATES)[number]) => {
    if (template.requiresConfiguration) {
      setSelectedTemplate(template);
      return;
    }
    await installMutation.mutateAsync({ template_key: template.installKey });
    closeModal();
  };

  if (selectedTemplate) {
    return (
      <DiscordTemplateInstallDialog
        template={selectedTemplate}
        onBack={() => setSelectedTemplate(null)}
        onClose={closeModal}
      />
    );
  }

  return (
    <ActionTemplateGallery
      testIdPrefix="discord"
      headerIcon={<DiscordIcon className="size-5" />}
      titleKey="discord_templates.title"
      descriptionKey="discord_templates.description"
      categories={DISCORD_CATEGORIES}
      templates={DISCORD_TEMPLATES}
      scratchTemplate={DISCORD_SCRATCH_TEMPLATE}
      scratchLabelKey="discord_templates.start_from_scratch"
      scratchDescriptionKey="discord_templates.start_from_scratch_description"
      actionIcon="FaDiscord"
      actionIconColor="#5865F2"
      isConnected={hasToken}
      isConnectionFetching={isTokenStatusFetching}
      templatesDisabled={!hasToken}
      onTemplateSelect={selectTemplate}
      connectionWarning={
        <div className="flex flex-col gap-1.5">
          <p className="font-semibold leading-snug">
            {t('discord_templates.connection_warning_title')}
          </p>
          <p className="leading-relaxed text-warning-foreground/75">
            {t('discord_templates.connection_warning')}
          </p>
          <button
            type="button"
            onClick={goToDiscordSetup}
            data-testid="discord-connect-button"
            className="mt-1 inline-flex cursor-pointer items-center gap-1 self-start rounded-sm font-medium text-warning underline decoration-warning/40 underline-offset-4 transition-colors hover:decoration-warning focus:outline-none focus-visible:ring-2 focus-visible:ring-warning/50 focus-visible:ring-offset-2 focus-visible:ring-offset-warning-muted"
          >
            {t('discord_templates.connect_discord')}
            <ArrowUpRight className="size-3.5" />
          </button>
        </div>
      }
    />
  );
};
